'use client'

import type { ReactNode } from 'react'
import type { SortDirection } from '@/components/ui/table'

import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input, InputGroupSearch } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { LinkCard } from '@/components/ui/link-card'
import { Pagination } from '@/components/ui/pagination'
import { BadgeStatus } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { personPhoto } from '@/constants/avatars'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Sparkline } from '@/components/charts/chart-parts'
import { SourceIcon } from '@/components/icons/source-icons'
import { TOOL_SPARKLINE } from '@/constants/chart-data'

import {
    ArrowRightIcon,
    CalendarIcon,
    DotsThreeIcon,
    LinkSimpleIcon,
    MegaphoneIcon,
    MoreIcon,
    PencilSimpleIcon,
    PlusIcon,
    TrashIcon,
    TreeStructureIcon,
    UsersIcon
} from '@/components/icons/icon-set'

import {
    AlertDialog,
    AlertDialogClose,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'

import {
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

import {
    Table,
    TableBody,
    TableCell,
    TableCellText,
    TableContainer,
    TableFooterBar,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

/*
 * GoodUnited / Table blocks. Figma: 24 / Blocks / Tables.
 * Every table sits in TableContainer: muted header row, 48px rows, muted footer bar with the
 * result count on the left and pagination or See all on the right. Tables scroll sideways on phones.
 */

type SortState<Key extends string> = { key: Key, direction: Exclude<SortDirection, 'none'> }

const nextSort = <Key extends string>(current: SortState<Key>, key: Key): SortState<Key> => current.key === key
    ? { key, direction: current.direction === 'ascending' ? 'descending' : 'ascending' }
    : { key, direction: 'ascending' }

const sortFor = <Key extends string>(state: SortState<Key>, key: Key): SortDirection | 'unsorted' => state.key === key ? state.direction : 'unsorted'

const compare = (a: string | number, b: string | number, direction: 'ascending' | 'descending') => {
    const result = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))
    return direction === 'ascending' ? result : -result
}

const money = (value: number) => `$${value.toFixed(2)}`

function RowActions({ label, items = ['View profile', 'Send message'] }: { label: string, items?: string[] }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant='outline' size='icon-sm' aria-label={`Actions for ${label}`} />}>
                <DotsThreeIcon aria-hidden='true' />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
                {items.map(item => (
                    <DropdownMenuItem key={item}>
                        {item}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

function KebabMenu({ label }: { label: string }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant='outline' size='icon-sm' aria-label={`More for ${label}`} />}>
                <MoreIcon aria-hidden='true' />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
                <DropdownMenuItem>
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem>
                    Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem variant='destructive'>
                    Archive
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

function DisplaySelect({ value, options, onChange }: { value: number, options: number[], onChange: (value: number) => void }) {
    return (
        <span className='flex items-center gap-2'>
            Display
            <Select value={String(value)} onValueChange={next => onChange(Number(next))}>
                <SelectTrigger size='sm' className='w-16 bg-input-background' aria-label='Rows per page'>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent align='end' className='min-w-24'>
                    {options.map(option => (
                        <SelectItem key={option} value={String(option)}>
                            {option}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </span>
    )
}

function SeeAll() {
    return (
        <Button size='sm'>
            See all
            <ArrowRightIcon aria-hidden='true' />
        </Button>
    )
}

function ToolIcon({ children }: { children: ReactNode }) {
    return (
        <span className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-muted text-icon-secondary [&_svg]:size-4' aria-hidden='true'>
            {children}
        </span>
    )
}

function StackedValue({ value, label }: { value: ReactNode, label: string }) {
    return (
        <div className='flex flex-col'>
            <span className='text-base font-medium text-text-primary'>
                {value}
            </span>
            <span className='text-sm whitespace-nowrap text-text-secondary'>
                {label}
            </span>
        </div>
    )
}

const SUPPORTERS = [
    { name: 'Jacob Jones', email: 'jacob.jones@example.com', source: 'Facebook Direct', joined: '12/4/17', joinedSort: 20171204, fundraisers: 4, donations: 5, total: 169.43 },
    { name: 'Jerome Bell', email: 'jerome.bell@example.com', source: 'Platform', joined: '5/27/15', joinedSort: 20150527, fundraisers: 2, donations: 5, total: 779.58 },
    { name: 'Darlene Robertson', email: 'darlene.r@example.com', source: 'Messenger Ads', joined: '12/10/13', joinedSort: 20131210, fundraisers: 1, donations: 5, total: 576.28 },
    { name: 'Savannah Nguyen', email: 'savannah.n@example.com', source: 'Challenges', joined: '6/19/14', joinedSort: 20140619, fundraisers: 2, donations: 5, total: 406.27 },
    { name: 'Jenny Wilson', email: 'jenny.wilson@example.com', source: 'Platform', joined: '3/4/16', joinedSort: 20160304, fundraisers: 5, donations: 5, total: 446.61 },
    { name: 'Bessie Cooper', email: 'bessie.cooper@example.com', source: 'Facebook Direct', joined: '10/28/12', joinedSort: 20121028, fundraisers: 5, donations: 5, total: 630.44 },
    { name: 'Annette Black', email: 'annette.black@example.com', source: 'Challenges', joined: '4/21/12', joinedSort: 20120421, fundraisers: 3, donations: 5, total: 396.84 },
    { name: 'Brooklyn Simmons', email: 'brooklyn.s@example.com', source: 'Messenger Ads', joined: '8/16/13', joinedSort: 20130816, fundraisers: 1, donations: 5, total: 490.51 }
]

type DefaultKey = 'name' | 'email' | 'source' | 'joined' | 'total'

/* Table/Default: plain records with sortable headers and a result count. */
export function TableDefault() {
    const [sort, setSort] = useState<SortState<DefaultKey>>({ key: 'name', direction: 'ascending' })
    const rows = useMemo(() => SUPPORTERS.slice(0, 5).sort((a, b) => {
        const key = sort.key === 'joined' ? 'joinedSort' : sort.key
        return compare(a[key], b[key], sort.direction)
    }), [sort])

    const head = (key: DefaultKey, label: string, align: 'start' | 'end' = 'start') => (
        <TableHead align={align} sort={sortFor(sort, key)} onSort={() => setSort(nextSort(sort, key))}>
            {label}
        </TableHead>
    )

    return (
        <TableContainer>
            <Table>
                <TableHeader>
                    <TableRow>
                        {head('name', 'Name')}
                        {head('email', 'Email')}
                        {head('source', 'Source')}
                        {head('joined', 'Joined')}
                        {head('total', 'Total given', 'end')}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={row.name}>
                            <TableCell className='whitespace-nowrap'>
                                {row.name}
                            </TableCell>
                            <TableCell>
                                {row.email}
                            </TableCell>
                            <TableCell className='whitespace-nowrap'>
                                {row.source}
                            </TableCell>
                            <TableCell>
                                {row.joined}
                            </TableCell>
                            <TableCell align='end' className='tabular-nums'>
                                {money(row.total)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TableFooterBar>
                Showing 1 to 5 of 5 supporters
            </TableFooterBar>
        </TableContainer>
    )
}

const SUPPORTER_TOTAL = 2653786

/* Table/Supporters: selectable rows, avatar names, row actions, page size and pagination. */
export function TableSupporters() {
    const [selected, setSelected] = useState<string[]>([])
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)
    const pageCount = Math.ceil(SUPPORTER_TOTAL / pageSize)
    const rows = useMemo(() => [...SUPPORTERS].sort((a, b) => compare(a.name, b.name, sortDirection)), [sortDirection])
    const allSelected = selected.length === rows.length
    const first = (page - 1) * pageSize + 1
    const last = Math.min(page * pageSize, SUPPORTER_TOTAL)

    const toggleRow = (name: string) => setSelected(current => current.includes(name) ? current.filter(item => item !== name) : [...current, name])

    return (
        <TableContainer>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className='w-12 pr-0'>
                            <Checkbox
                                checked={allSelected}
                                indeterminate={selected.length > 0 && !allSelected}
                                onCheckedChange={checked => setSelected(checked ? rows.map(row => row.name) : [])}
                                aria-label='Select all supporters'
                            />
                        </TableHead>
                        <TableHead sort={sortDirection} onSort={() => setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending')}>
                            Name
                        </TableHead>
                        <TableHead>
                            Subscriber
                        </TableHead>
                        <TableHead>
                            Last Interaction Date
                        </TableHead>
                        <TableHead>
                            Fundraisers Created
                        </TableHead>
                        <TableHead>
                            # Donations
                        </TableHead>
                        <TableHead>
                            Total Contributions
                        </TableHead>
                        <TableHead className='w-14'>
                            <span className='sr-only'>
                                Actions
                            </span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={row.name} selected={selected.includes(row.name)}>
                            <TableCell className='pr-0'>
                                <Checkbox checked={selected.includes(row.name)} onCheckedChange={() => toggleRow(row.name)} aria-label={`Select ${row.name}`} />
                            </TableCell>
                            <TableCell>
                                <span className='flex items-center gap-2 whitespace-nowrap'>
                                    <Avatar size='lg'>
                                        {personPhoto(row.name, row.name) && <AvatarImage src={personPhoto(row.name, row.name)} alt='' />}
                                        <AvatarFallback>
                                            {row.name.split(' ').map(part => part[0]).join('')}
                                        </AvatarFallback>
                                    </Avatar>
                                    {row.name}
                                </span>
                            </TableCell>
                            <TableCell>
                                <SourceIcon source='facebook' className='size-5 text-icon-secondary' aria-label='Facebook subscriber' />
                            </TableCell>
                            <TableCell>
                                {row.joined}
                            </TableCell>
                            <TableCell>
                                {row.fundraisers}
                            </TableCell>
                            <TableCell>
                                {row.donations}
                            </TableCell>
                            <TableCell className='tabular-nums'>
                                {money(row.total)}
                            </TableCell>
                            <TableCell align='end'>
                                <RowActions label={row.name} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TableFooterBar>
                <span>
                    {`Showing ${first.toLocaleString('en-US')} to ${last.toLocaleString('en-US')} of ${SUPPORTER_TOTAL.toLocaleString('en-US')} supporters`}
                </span>
                <span className='flex flex-wrap items-center gap-4'>
                    <DisplaySelect value={pageSize} options={[10, 25, 50, 100]} onChange={size => { setPageSize(size); setPage(1) }} />
                    <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
                </span>
            </TableFooterBar>
        </TableContainer>
    )
}

export interface AiLink {
    id: number
    url: string
    description: string
}

const INITIAL_LINKS: AiLink[] = Array.from({ length: 20 }, (_, index) => ({
    id: index + 1,
    url: `goodunited.com/link${index + 1}`,
    description: index % 4 === 3 ? '' : 'Add your main site or campaign pages so we can review your content.'
}))

type LinkKey = 'url' | 'description'

/*
 * Table/AiLinks: header action, search row, edit and delete per row, page size and pagination.
 * initialLinks seeds the rows (the writing-style settings pass their own); className goes on the container.
 */
export function TableAiLinks({ initialLinks = INITIAL_LINKS, className }: { initialLinks?: AiLink[], className?: string } = {}) {
    const [links, setLinks] = useState<AiLink[]>(initialLinks)
    const [query, setQuery] = useState('')
    const [sort, setSort] = useState<SortState<LinkKey>>({ key: 'url', direction: 'ascending' })
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(4)
    const [editing, setEditing] = useState<AiLink | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [draft, setDraft] = useState({ url: '', description: '' })
    const [error, setError] = useState('')
    const [removing, setRemoving] = useState<AiLink | null>(null)

    const filtered = useMemo(() => links
        .filter(link => `${link.url} ${link.description}`.toLowerCase().includes(query.trim().toLowerCase()))
        .sort((a, b) => sort.key === 'url' ? compare(a.id, b.id, sort.direction) : compare(a.description, b.description, sort.direction)), [links, query, sort])

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
    const current = Math.min(page, pageCount)
    const rows = filtered.slice((current - 1) * pageSize, current * pageSize)
    const first = filtered.length ? (current - 1) * pageSize + 1 : 0
    const last = Math.min(current * pageSize, filtered.length)

    const openDialog = (link: AiLink | null) => {
        setEditing(link)
        setDraft({ url: link?.url ?? '', description: link?.description ?? '' })
        setError('')
        setDialogOpen(true)
    }

    const save = () => {
        const url = draft.url.trim()
        if (!url) {
            setError('Add the link you want the AI to use.')
            return
        }
        if (editing) {
            setLinks(items => items.map(item => item.id === editing.id ? { ...item, url, description: draft.description.trim() } : item))
        }
        else {
            setLinks(items => [{ id: Math.max(0, ...items.map(item => item.id)) + 1, url, description: draft.description.trim() }, ...items])
            setSort({ key: 'url', direction: 'descending' })
            setPage(1)
        }
        setDialogOpen(false)
    }

    return (
        <TableContainer className={className}>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead sort={sortFor(sort, 'url')} onSort={() => setSort(nextSort(sort, 'url'))}>
                            Links used by AI
                        </TableHead>
                        <TableHead sort={sortFor(sort, 'description')} onSort={() => setSort(nextSort(sort, 'description'))}>
                            Description
                        </TableHead>
                        <TableHead align='end' className='py-1'>
                            <Button size='sm' onClick={() => openDialog(null)}>
                                Add link
                                <PlusIcon aria-hidden='true' />
                            </Button>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow className='hover:bg-transparent [tbody_&]:hover:bg-transparent'>
                        <TableCell colSpan={3} className='py-2'>
                            <InputGroupSearch
                                value={query}
                                onChange={event => {
                                    setQuery(event.target.value)
                                    setPage(1)
                                }}
                                aria-label='Search links'
                            />
                        </TableCell>
                    </TableRow>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={3} className='py-8 text-center text-text-secondary'>
                                {query ? `No links match "${query}".` : 'No links yet. Add the pages the AI should read.'}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map(link => (
                        <TableRow key={link.id}>
                            <TableCell className='whitespace-nowrap'>
                                {link.url}
                            </TableCell>
                            <TableCell className='min-w-64 text-sm text-text-secondary'>
                                {link.description}
                            </TableCell>
                            <TableCell align='end'>
                                <span className='inline-flex gap-2'>
                                    <Button variant='outline' size='icon-sm' onClick={() => openDialog(link)} aria-label={`Edit ${link.url}`}>
                                        <PencilSimpleIcon aria-hidden='true' />
                                    </Button>
                                    <Button variant='outline' size='icon-sm' onClick={() => setRemoving(link)} aria-label={`Delete ${link.url}`}>
                                        <TrashIcon aria-hidden='true' />
                                    </Button>
                                </span>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TableFooterBar>
                <span>
                    {`Showing ${first} to ${last} of ${filtered.length} links`}
                </span>
                <span className='flex flex-wrap items-center gap-4'>
                    <DisplaySelect value={pageSize} options={[4, 10, 20]} onChange={size => { setPageSize(size); setPage(1) }} />
                    <Pagination page={current} pageCount={pageCount} onPageChange={setPage} siblings={1} />
                </span>
            </TableFooterBar>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <div className='flex flex-col gap-1'>
                            <DialogTitle>
                                {editing ? 'Edit link' : 'Add link'}
                            </DialogTitle>
                            <DialogDescription>
                                The AI reads these pages to learn about your organization.
                            </DialogDescription>
                        </div>
                        <DialogCloseButton />
                    </DialogHeader>
                    <form
                        className='flex flex-col gap-5'
                        onSubmit={event => {
                            event.preventDefault()
                            save()
                        }}
                    >
                        <Field>
                            <FieldLabel htmlFor='ai-link-url'>
                                Link
                            </FieldLabel>
                            <Input id='ai-link-url' value={draft.url} aria-invalid={!!error} onChange={event => setDraft({ ...draft, url: event.target.value })} placeholder='goodunited.com/campaign' />
                            <FieldError>
                                {error}
                            </FieldError>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor='ai-link-description'>
                                Description
                            </FieldLabel>
                            <Textarea id='ai-link-description' value={draft.description} onChange={event => setDraft({ ...draft, description: event.target.value })} className='min-h-24' />
                        </Field>
                        <DialogFooter>
                            <Button type='submit' size='lg'>
                                {editing ? 'Save changes' : 'Add link'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!removing} onOpenChange={open => !open && setRemoving(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader tone='destructive'>
                        <AlertDialogTitle>
                            Delete this link?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {`The AI stops reading ${removing?.url ?? 'this page'}. You can add it again later.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogClose render={<Button variant='outline' />}>
                            Cancel
                        </AlertDialogClose>
                        <Button
                            variant='destructive'
                            onClick={() => {
                                if (removing) setLinks(items => items.filter(item => item.id !== removing.id))
                                setRemoving(null)
                            }}
                        >
                            Delete link
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </TableContainer>
    )
}

const TOOLS = [
    { id: 'tool-1', status: 'active' as const },
    { id: 'tool-2', status: 'draft' as const },
    { id: 'tool-3', status: 'draft' as const }
]

/* Table/ToolPerformance: status, totals with a sparkline, metrics and a See Flow link card. */
export function TableToolPerformance() {
    return (
        <TableContainer>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead sort='unsorted'>
                            Tool used
                        </TableHead>
                        <TableHead sort='unsorted'>
                            Status
                        </TableHead>
                        <TableHead colSpan={6}>
                            <span className='sr-only'>
                                Performance
                            </span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {TOOLS.map(tool => (
                        <TableRow key={tool.id}>
                            <TableCell>
                                <TableCellText strong primary='You Are Not Alone' supporting='03/20/2024' icon={<ToolIcon><TreeStructureIcon /></ToolIcon>} />
                            </TableCell>
                            <TableCell>
                                <BadgeStatus status={tool.status} />
                            </TableCell>
                            <TableCell>
                                <StackedValue value='$1,335,148' label='Total Subscribers' />
                            </TableCell>
                            <TableCell>
                                <Sparkline values={TOOL_SPARKLINE} width={96} />
                            </TableCell>
                            <TableCell align='center'>
                                <StackedValue value='27' label='Visits' />
                            </TableCell>
                            <TableCell>
                                <StackedValue value='27' label='Subscribers' />
                            </TableCell>
                            <TableCell>
                                <StackedValue value='30%' label='Open Rate' />
                            </TableCell>
                            <TableCell align='end'>
                                <span className='inline-flex items-center gap-3'>
                                    <LinkCard icon={<TreeStructureIcon />} label='See Flow' className='w-40' />
                                    <KebabMenu label='You Are Not Alone' />
                                </span>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TableFooterBar>
                Showing 1 to 3 of 20 segments
                <SeeAll />
            </TableFooterBar>
        </TableContainer>
    )
}

/* Table/ToolSubscribers: totals and dates, then either metrics or link cards to the post and its journey. */
export function TableToolSubscribers() {
    return (
        <TableContainer>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead sort='unsorted'>
                            Tool used
                        </TableHead>
                        <TableHead sort='unsorted'>
                            Subscribers
                        </TableHead>
                        <TableHead sort='unsorted'>
                            Date
                        </TableHead>
                        <TableHead colSpan={3}>
                            <span className='sr-only'>
                                Details
                            </span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <TableCellText strong primary='Mauris Nibh Praesent' supporting='03/20/2024' icon={<ToolIcon><UsersIcon /></ToolIcon>} />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='185' label='Total Subscribers' />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='Dec 19, 2024' label='Last Opt-in Date' />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='27' label='Ad Spend' />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='185' label='Cost Per Subscriber' />
                        </TableCell>
                        <TableCell align='end'>
                            <KebabMenu label='Mauris Nibh Praesent' />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <TableCellText strong primary='Potenti Gravida Pretium Tor' supporting='03/20/2024' icon={<ToolIcon><CalendarIcon /></ToolIcon>} />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='583' label='Total Subscribers' />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='Dec 19, 2024' label='Last Opt-in Date' />
                        </TableCell>
                        <TableCell>
                            <LinkCard icon={<LinkSimpleIcon />} label='Facebook Post' className='w-52' />
                        </TableCell>
                        <TableCell>
                            <LinkCard icon={<MegaphoneIcon />} caption='Associated Journey' label='UAT Cate Jorge2' className='w-52' />
                        </TableCell>
                        <TableCell align='end'>
                            <KebabMenu label='Potenti Gravida Pretium Tor' />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <TableCellText strong primary='You Are Not Alone URL' supporting='03/20/2024' icon={<ToolIcon><LinkSimpleIcon /></ToolIcon>} />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='583' label='Total Subscribers' />
                        </TableCell>
                        <TableCell>
                            <StackedValue value='Dec 19, 2024' label='Last Opt-in Date' />
                        </TableCell>
                        <TableCell>
                            <LinkCard icon={<LinkSimpleIcon />} caption='Journey URL' label='https://m.me/stopsoldiersuicide' href='https://m.me/stopsoldiersuicide' action='copy' className='w-52' />
                        </TableCell>
                        <TableCell>
                            <LinkCard icon={<MegaphoneIcon />} caption='Associated Journey' label='UAT Cate Jorge2' className='w-52' />
                        </TableCell>
                        <TableCell align='end'>
                            <KebabMenu label='You Are Not Alone URL' />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <TableFooterBar>
                Showing 1 to 3 of 20 segments
                <SeeAll />
            </TableFooterBar>
        </TableContainer>
    )
}
